/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

/** 
  All of the routes for the Material Dashboard 2 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  11. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 PRO React layouts
import Icon from '@mui/material/Icon'

import profilePicture from 'assets/images/account-icons/default-white.png'
import MDAvatar from 'components/MDAvatar'
import { ROLE_LEVEL } from 'constants/roles'
import Settings from 'layouts/account'
import ResetPassword from 'layouts/authentication/reset-password'
import SignIn from 'layouts/authentication/sign-in'
import BusinessAccount from 'layouts/business-account'
import BusinessAccountTable from 'layouts/business-account-table'
import ConfigFeedback from 'layouts/configuration/feedback'
import Import from 'layouts/configuration/import'
import ConfigOrganisation from 'layouts/configuration/organisation'
import Analytics from 'layouts/dashboards/analytics'
import CustomerFeedbackPage from 'layouts/feedback/customer'
import FeedbackPage from 'layouts/feedback/internal'
import FeedbackTable from 'layouts/feedback-table'
import NewUserForm from 'layouts/new-user'
import OrganisationMemberPage from 'layouts/organisation-member'
import OrganisationMembersTable from 'layouts/organisation-members-table'
import StripeCancelPage from 'layouts/stripe/CancelPage'
import StripeSuccessPage from 'layouts/stripe/SuccessPage'
import { ROUTE_PATHS } from 'routes/routePaths'
import { RouteType } from 'routes/types'

const routes: RouteType[] = [
	{
		type: 'collapse',
		name: `\${email}`,
		key: 'user-account-sidebar',
		icon: <MDAvatar src={profilePicture} alt="Brooklyn Alice" size="xs" />,
		route: ROUTE_PATHS.ACCOUNT.SETTINGS,
		component: <Settings />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[5],
	},
	{
		type: 'invisible',
		name: 'New User',
		key: 'new-user',
		route: ROUTE_PATHS.ACCOUNT.NEW_USER,
		component: <NewUserForm />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[2],
	},
	{
		type: 'invisible',
		name: 'Stripe Payment Cancelled',
		key: 'stripe-cancel',
		route: ROUTE_PATHS.STRIPE.CANCEL,
		component: <StripeCancelPage />,
		authRequired: true,
		noCollapse: true,
	},
	{
		type: 'invisible',
		name: 'Stripe Payment Success',
		key: 'stripe-success',
		route: ROUTE_PATHS.STRIPE.SUCCESS,
		component: <StripeSuccessPage />,
		authRequired: true,
		noCollapse: true,
	},
	{
		name: 'New Feedback',
		type: 'collapse',
		key: 'new-feedback',
		icon: <Icon fontSize="medium">add_circle_outline</Icon>,
		component: <FeedbackPage />,
		route: ROUTE_PATHS.FEEDBACK.NEW,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[5],
	},
	{ type: 'divider', key: 'divider-anayltics' },
	{
		type: 'collapse',
		icon: <Icon fontSize="medium">dashboard</Icon>,
		name: 'Analytics',
		key: 'analytics',
		route: ROUTE_PATHS.DASHBOARD.ANALYTICS,
		component: <Analytics />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[4],
	},
	{
		type: 'collapse',
		name: 'Feedback Overview',
		key: 'feedback-overview',
		icon: <Icon fontSize="medium">receipt</Icon>,
		route: ROUTE_PATHS.FEEDBACK.TABLE,
		component: <FeedbackTable />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[4],
	},
	{
		type: 'invisible',
		name: 'Sign In',
		key: 'sign-in',
		route: ROUTE_PATHS.AUTHENTICATION.SIGN_IN,
		component: <SignIn />,
		authRequired: false,
	},
	{
		type: 'invisible',
		name: 'Reset Password',
		key: 'reset-password',
		route: ROUTE_PATHS.AUTHENTICATION.RESET_PASSWORD,
		component: <ResetPassword />,
		authRequired: false,
	},
	{
		type: 'invisible',
		name: 'Feedback Detail',
		key: 'feedback-detail',
		route: ROUTE_PATHS.FEEDBACK.DETAIL,
		component: <FeedbackPage />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[4],
	},
	{
		type: 'invisible',
		name: 'Customer Feedback',
		key: 'customer-feedback',
		route: ROUTE_PATHS.FEEDBACK.CUSTOMER,
		component: <CustomerFeedbackPage />,
		noCollapse: true,
	},
	{
		type: 'collapse',
		name: 'Organisation Member',
		key: 'organisation-member',
		route: ROUTE_PATHS.ORGANISATION_MEMBER.BASE,
		icon: <Icon fontSize="medium">groups</Icon>,
		component: <OrganisationMembersTable />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[4],
	},
	{
		type: 'invisible',
		name: 'Organisation Member Detail',
		key: 'organisation-member-detail',
		route: ROUTE_PATHS.ORGANISATION_MEMBER.DETAIL,
		component: <OrganisationMemberPage />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[4],
	},
	{
		type: 'collapse',
		name: 'Business Accounts',
		key: 'business-accounts-overview',
		icon: <Icon fontSize="medium">store</Icon>,
		route: ROUTE_PATHS.BUSINESS_ACCOUNTS.BASE,
		component: <BusinessAccountTable />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[4],
	},
	{
		type: 'invisible',
		name: 'Business Account Detail',
		key: 'business-account-detail',
		route: ROUTE_PATHS.BUSINESS_ACCOUNTS.DETAIL,
		component: <BusinessAccount />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[4],
	},
	{
		type: 'invisible',
		name: 'Business Accounts New Form',
		key: 'business-accounts-new-form',
		route: ROUTE_PATHS.BUSINESS_ACCOUNTS.NEW,
		component: <BusinessAccount />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[3],
	},

	{
		type: 'invisible',
		name: 'Organisation Members New Form',
		key: 'organisation-members-new-form',
		route: ROUTE_PATHS.ORGANISATION_MEMBER.NEW,
		component: <OrganisationMemberPage />,
		authRequired: true,
		noCollapse: true,
		roleRequired: ROLE_LEVEL[3],
	},
	{ type: 'divider', key: 'divider-configration', roleRequired: ROLE_LEVEL[2] },
	{
		type: 'title',
		title: 'Configuration',
		key: 'title-configuration',
		roleRequired: ROLE_LEVEL[2],
	},
	{
		type: 'collapse',
		name: 'Configuration',
		key: 'configuration',
		route: ROUTE_PATHS.CONFIG.BASE,
		icon: <Icon fontSize="medium">settings</Icon>,
		authRequired: true,
		roleRequired: ROLE_LEVEL[2],
		collapse: [
			{
				name: 'Organisation',
				key: 'configuration-organisation',
				route: ROUTE_PATHS.CONFIG.ORGANISATION,
				component: <ConfigOrganisation />,
				authRequired: true,
				roleRequired: ROLE_LEVEL[2],
			},
			{
				name: 'Feedback',
				key: 'configuration-feedback',
				route: ROUTE_PATHS.CONFIG.FEEDBACK,
				component: <ConfigFeedback />,
				authRequired: true,
				roleRequired: ROLE_LEVEL[2],
			},
			{
				name: 'Import',
				key: 'import',
				route: ROUTE_PATHS.CONFIG.IMPORT,
				component: <Import />,
				authRequired: true,
				roleRequired: ROLE_LEVEL[2],
			},
		],
	},
]

export default routes
